/**
 * Cookie Consent Store
 *
 * Central module for all consent state: reading, writing, versioning,
 * GTM Consent Mode v2 signalling, and subscriber notification.
 *
 * BANNER_VERSION: bump whenever the banner copy/design changes materially.
 * POLICY_VERSION: bump whenever the privacy policy or cookie categories change.
 *
 * Bumping either constant invalidates all stored choices and re-shows the
 * banner on the visitor's next page load. Both constants are also embedded
 * in the inline bootstrap script in client/index.html — keep them in sync
 * there too if you change them here.
 */

export const BANNER_VERSION = "1.0";
export const POLICY_VERSION = "2026-10";

export interface ConsentGrants {
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
}

export type ConsentChoiceKind = "accept_all" | "essential_only" | "custom" | "changed";

export interface ConsentRecord {
  schemaVersion: number;
  visitorUuid: string;
  bannerVersion: string;
  policyVersion: string;
  grants: ConsentGrants;
  choiceKind: ConsentChoiceKind;
  decidedAt: string;
}

const STORAGE_KEY = "veras_cookie_consent";
const COOKIE_NAME = "veras_consent";
const SCHEMA_VERSION = 1;

export const ALL_GRANTS: ConsentGrants = {
  analytics: true,
  marketing: true,
  personalization: true,
};

export const ESSENTIAL_ONLY_GRANTS: ConsentGrants = {
  analytics: false,
  marketing: false,
  personalization: false,
};

function generateUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function setCookieValue(name: string, value: string, days: number) {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  } catch {}
}

function deleteCookieValue(name: string) {
  try {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  } catch {}
}

function getOrCreateVisitorUuid(existing?: ConsentRecord | null): string {
  if (existing?.visitorUuid) return existing.visitorUuid;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ConsentRecord;
      if (parsed.visitorUuid) return parsed.visitorUuid;
    }
  } catch {}
  return generateUuid();
}

export function getConsent(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentRecord;
  } catch {
    return null;
  }
}

export function isConsentStale(record: ConsentRecord | null): boolean {
  if (!record) return true;
  if (record.schemaVersion !== SCHEMA_VERSION) return true;
  if (record.bannerVersion !== BANNER_VERSION) return true;
  if (record.policyVersion !== POLICY_VERSION) return true;
  return false;
}

export function setConsent(
  grants: ConsentGrants,
  choiceKind: ConsentChoiceKind,
  existingRecord?: ConsentRecord | null,
): ConsentRecord {
  const visitorUuid = getOrCreateVisitorUuid(existingRecord);
  const record: ConsentRecord = {
    schemaVersion: SCHEMA_VERSION,
    visitorUuid,
    bannerVersion: BANNER_VERSION,
    policyVersion: POLICY_VERSION,
    grants,
    choiceKind,
    decidedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setCookieValue(
      COOKIE_NAME,
      JSON.stringify({ grants, policyVersion: POLICY_VERSION }),
      365,
    );
  } catch {}
  applyConsentToDataLayer(grants);
  notifyListeners(record);
  return record;
}

export function clearConsent() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    deleteCookieValue(COOKIE_NAME);
  } catch {}
}

/**
 * Push a Consent Mode v2 `update` command to the GTM dataLayer.
 * Called on every consent decision and on page load for returning visitors.
 *
 * The `gtag` global function is defined in index.html before GTM loads and
 * correctly formats the IArguments object that GTM's consent engine expects.
 * We call it if available; otherwise we push the compact array form directly
 * so the update is buffered for GTM to pick up when it loads.
 */
export function applyConsentToDataLayer(grants: ConsentGrants) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  const dl = window.dataLayer;
  const consentPayload = {
    analytics_storage: grants.analytics ? "granted" : "denied",
    ad_storage: grants.marketing ? "granted" : "denied",
    ad_user_data: grants.marketing ? "granted" : "denied",
    ad_personalization: grants.marketing ? "granted" : "denied",
    personalization_storage: grants.personalization ? "granted" : "denied",
  };

  // index.html defines window.gtag before React boots — use it when available.
  const gtagFn = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtagFn === "function") {
    gtagFn("consent", "update", consentPayload);
    return;
  }

  // Fallback: window.gtag is absent only in extreme edge cases (no-JS hydration,
  // aggressive CSP, unit tests). Use a non-arrow IIFE so `arguments` is the
  // native IArguments object that GTM's consent engine distinguishes from a
  // plain array when reading the dataLayer queue.
  // eslint-disable-next-line prefer-rest-params
  (function (this: void) { dl.push(arguments as never); } as (...a: unknown[]) => void)(
    "consent", "update", consentPayload,
  );
}

type ConsentListener = (record: ConsentRecord) => void;
const listeners = new Set<ConsentListener>();

export function subscribe(listener: ConsentListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyListeners(record: ConsentRecord) {
  for (const listener of listeners) {
    try {
      listener(record);
    } catch {}
  }
}

/**
 * Fire the consent audit event to the server (fire-and-forget; never
 * blocks the UI on it). Called on every consent decision.
 */
export function recordConsentEvent(
  record: ConsentRecord,
  pageUrl: string,
): void {
  try {
    fetch("/api/consent", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visitorUuid: record.visitorUuid,
        policyVersion: record.policyVersion,
        bannerVersion: record.bannerVersion,
        choiceKind: record.choiceKind,
        grants: record.grants,
        pageUrl,
      }),
    }).catch(() => {});
  } catch {}
}
